import { zodResolver } from "@hookform/resolvers/zod";
import { useState } from "react";
import { useForm, UseFormReturn } from "react-hook-form";
import { NavigateFunction, useNavigate } from "react-router-dom";

import type { UseApproveUnlock } from "@/@types/unlock-request";
import { toast } from "@/components/ui/use-toast";
import { UNLOCK_DETAIL_LABELS, UNLOCK_TOASTS } from "@/constants/messages";
import { AccessRequestDTO } from "@/services/dtos/accessRequest.dto";
import { useApproveUnlockRequestMutation } from "@/services/request-access";
import { extractRtkErrorMessage } from "@/utils/extract-rtk-error";

import {
  UnlockApproveFormSchema,
  UnlockApproveFormValues,
} from "./validate-form";

export function useApproveUnlock(
  request: AccessRequestDTO,
  backPath: string,
): UseApproveUnlock {
  const navigate: NavigateFunction = useNavigate();
  const [isConfirmOpen, setConfirmOpen] = useState<boolean>(false);
  const [approveUnlockRequest, { isLoading: isApproving }] =
    useApproveUnlockRequestMutation();

  const form: UseFormReturn<UnlockApproveFormValues> =
    useForm<UnlockApproveFormValues>({
      resolver: zodResolver(UnlockApproveFormSchema),
      mode: "onChange",
      defaultValues: { comment: "" },
    });

  const approve = async (values: UnlockApproveFormValues): Promise<void> => {
    try {
      await approveUnlockRequest({
        id: request.id,
        version: request.version,
        comment: values.comment.trim() || undefined,
      }).unwrap();

      setConfirmOpen(false);
      toast(UNLOCK_TOASTS.APPROVED(request.name));
      navigate(backPath);
    } catch (error) {
      setConfirmOpen(false);
      toast(
        UNLOCK_TOASTS.APPROVE_FAILED(
          extractRtkErrorMessage(
            error,
            UNLOCK_DETAIL_LABELS.APPROVE_FALLBACK_ERROR,
          ),
        ),
      );
    }
  };

  const changeConfirmOpen = (open: boolean): void => {
    if (isApproving) return;
    setConfirmOpen(open);
    if (!open) form.reset();
  };

  return {
    form,
    isConfirmOpen,
    isApproving,
    openConfirm: (): void => setConfirmOpen(true),
    changeConfirmOpen,
    confirm: form.handleSubmit(approve),
  };
}
